import React, { useEffect, useState } from 'react';
import { FiPlus, FiArrowRight, FiBookOpen, FiBook, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { nextClassLine } from './instructorHome';
import styles from './InstructorHome.module.css';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** One big number that opens the matching page for the course. */
function Stat({ value, label, onClick, ariaLabel, tone }) {
  return (
    <button type="button" className={`${styles.k3Stat} ${tone ? styles[tone] : ''}`} onClick={onClick} aria-label={ariaLabel}>
      <span className={styles.k3Num}>{value}</span>
      <span className={styles.k3Label}>{label}</span>
    </button>
  );
}

/**
 * My courses (design K3): one course at a time, with its numbers up front.
 * Students, to grade and attendance each open the matching page for that
 * course; the arrows switch between courses.
 */
export default function CourseCards({
  courses, allCount, showPrevious, onTogglePrevious, toGradeByCourse, cancellations = [],
  onAddContent, onOpen, onStudents, onGradebook, onAttendance,
}) {
  const [index, setIndex] = useState(0);
  const hasPrevious = allCount > courses.length || showPrevious;
  useEffect(() => { if (index >= courses.length) setIndex(0); }, [courses.length, index]);

  if (courses.length === 0) {
    return (
      <section className={styles.k3} aria-labelledby="my-courses-title">
        <h3 id="my-courses-title" className={styles.srOnly}>My courses</h3>
        <div className={styles.k3Empty}>
          <FiBookOpen size={18} aria-hidden="true" />
          <p>{allCount > 0 ? 'No current courses.' : 'No courses assigned yet.'}</p>
          {hasPrevious && (
            <button type="button" className={styles.linkBtn} onClick={onTogglePrevious}>
              {showPrevious ? 'Hide' : 'Show'} previous courses
            </button>
          )}
        </div>
      </section>
    );
  }

  const c = courses[Math.min(index, courses.length - 1)];
  const many = courses.length > 1;
  const students = num(c.enrolled_students);
  const toGrade = toGradeByCourse.get(c.id) || 0;
  const rate = c.attendance_rate === null || c.attendance_rate === undefined ? null : num(c.attendance_rate);
  const step = (d) => setIndex((i) => (i + d + courses.length) % courses.length);

  return (
    <section className={styles.k3} aria-labelledby="my-courses-title">
      <h3 id="my-courses-title" className={styles.srOnly}>My courses</h3>
      <div className={styles.k3Head}>
        <span className={styles.laneIcon} aria-hidden="true"><FiBook size={17} /></span>
        <div className={styles.k3Titles}>
          <b className={styles.k3Code}>{c.code}</b>
          <span className={styles.k3Name} title={c.name}>{c.name}</span>
        </div>
        {many && (
          <div className={styles.k3Pager} role="group" aria-label="Switch course">
            <button type="button" className={styles.k3PagerBtn} onClick={() => step(-1)} aria-label="Previous course"><FiChevronLeft size={15} /></button>
            <span className={styles.k3PagerText} aria-live="polite">{Math.min(index, courses.length - 1) + 1} of {courses.length}</span>
            <button type="button" className={styles.k3PagerBtn} onClick={() => step(1)} aria-label="Next course"><FiChevronRight size={15} /></button>
          </div>
        )}
      </div>

      <div className={styles.k3Stats}>
        <Stat
          value={students}
          label={students === 1 ? 'student' : 'students'}
          onClick={() => onStudents(c)}
          ariaLabel={`View enrolled students for ${c.code}`}
        />
        <Stat
          value={toGrade}
          label="to grade"
          tone={toGrade > 0 ? 'k3Warn' : undefined}
          onClick={() => onGradebook(c)}
          ariaLabel={`Open the gradebook for ${c.code}`}
        />
        <Stat
          value={rate === null ? '—' : `${rate}%`}
          label="attendance"
          onClick={() => onAttendance(c)}
          ariaLabel={`Open attendance for ${c.code}`}
        />
      </div>

      <div className={styles.k3Mid}>
        <span className={styles.k3Next}>{nextClassLine(c, undefined, cancellations.filter((x) => x.kind === 'course' && Number(x.id) === Number(c.id)))}</span>
        {hasPrevious && (
          <button type="button" className={styles.linkBtn} onClick={onTogglePrevious}>
            {showPrevious ? 'Hide' : 'Show'} previous courses
          </button>
        )}
      </div>

      <div className={styles.k3Foot}>
        <button type="button" className={`${styles.btn} ${styles.btnGo}`} onClick={() => onAddContent(c)}>
          <FiPlus size={15} aria-hidden="true" />Add content
        </button>
        <button type="button" className={styles.btn} onClick={() => onOpen(c)}>
          Open course<FiArrowRight size={14} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
